import { Button, ButtonVariantEnum, CalculateIcon, Info, Radio, TextInput } from '@Wonder-Cave/ui';
import { IDropdownValue } from '@Wonder-Cave/ui/dist/Autocomplete';
import { axiosPost } from '@gr/portal/authAxios';
import { useAuth0Context } from '@gr/portal/contexts/Auth0ConfigContext';
import { NotificationType, useNotifications } from '@gr/portal/contexts/NotificationContext';
import useCampaignTypesForClient from '@gr/portal/hooks/useCampaignTypesForClient';
import { getProductBaseDomainUrl } from '@gr/portal/providers/auth0-product.provider';
import { convertEnumToReadableString } from '@gr/portal/providers/utility.provider';
import { isFileLocalSize, isFileMediaType, isFileTollFreeSize } from '@gr/portal/providers/validation.provider';
import { BUCKETS, MessageTypeEnum, PreferredAreaCodeEnum, SystemNumberTypeEnum } from '@gr/shared/enums';
import {
  ICalculateLeadsRequest,
  ICampaignTypeWithAreaCodes,
  IHttpResponse,
  ISignedUrlRequest,
} from '@gr/shared/models';
import axios from 'axios';
import { FastField } from 'formik';
import { useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import ClientDropdown from '../../shared/Form/Dropdowns/ClientDropdown';
import AreaCodeDropdown from '../../shared/Form/Dropdowns/AreaCodeDropdown';
import NewAssignedAgentsDropdown from '../../shared/Form/Dropdowns/NewAssignedAgentsDropdown';
import NewCampaignTypeDropdown from '../../shared/Form/Dropdowns/NewCampaignTypeDropdown';
import NewContactListDropdown from '../../shared/Form/Dropdowns/NewContactListDropdown';
import NewStateDropdown from '../../shared/Form/Dropdowns/NewStateDropdown';
import NewSuppressionListDropdown from '../../shared/Form/Dropdowns/NewSuppressionListDropdown';
import PreferredAreaCodeDropdown from '../../shared/Form/Dropdowns/PreferredAreaCodeDropdown';
import { FileUpload } from '../../shared/Form/FileUpload';

interface IPlanProps {
  values?: any;
  errors?: any;
  touched?: any;
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void;
  setFieldTouched?: (field: string, touched: boolean) => void;
  setFieldError?: (field: string, msg: string | undefined) => void;
  handleChange?: () => void;
  handleBlur?: () => void;
  onSubmit: (formikProps) => void;
  setLeads: (leads: any) => void;
  setLoading: (value: boolean) => void;
  allListsFailed: boolean;
  leads?: number;
  submissionError?: string;
  formErrors?: { [key: string]: string };
}

const Plan = ({
  values,
  errors,
  touched,
  setFieldValue,
  setFieldTouched,
  setFieldError,
  handleChange,
  handleBlur,
  setLeads,
  setLoading,
  allListsFailed,
  leads,
  submissionError,
  formErrors,
}: IPlanProps) => {
  const { data, isLoading: loading } = useCampaignTypesForClient(values.client?.value, values.messageType);
  const campaignTypes: ICampaignTypeWithAreaCodes[] = data?.types ?? [];
  const dropdownValues: IDropdownValue[] = campaignTypes.map((ct) => ({
    label: ct.campaignType === 'GOTV' ? 'GOTV' : convertEnumToReadableString(ct.campaignType), // We want GOTV to remain full caps
    value: ct.campaignType,
  }));
  const preferredAreaCodeOptions: IDropdownValue[] = [];
  const currentCampaignType = campaignTypes.find((ct) => ct.campaignType === values.campaignType);

  currentCampaignType?.areaCodes?.forEach((ac) => {
    if (ac.numberCount >= 1 || ac.areaCode === PreferredAreaCodeEnum.TOLL_FREE) {
      preferredAreaCodeOptions.push({
        label: ac.areaCode,
        value: ac.areaCode,
      });
    }
  }) ?? [];

  const [sendingNumberError, setSendingNumberError] = useState('');
  const [calculateLoading, setCalculateLoading] = useState(false);
  const { auth0Config } = useAuth0Context();
  const { addNotification } = useNotifications();
  errors = { ...errors, ...formErrors };

  // Generation counter for the volume calculation. Bumped both when a calculation starts and when
  // one is invalidated, so a response that arrives after the user has moved on can be discarded.
  const leadsRequestIdRef = useRef(0);

  // Any change to an input that feeds the request invalidates the displayed volume. Without this
  // the Build step keeps rendering the previous number as "N messages scheduled to send at …" —
  // phrased as a commitment — for a selection it was never calculated against.
  const invalidateLeads = () => {
    leadsRequestIdRef.current += 1;
    setLeads(undefined);
    // Release the button immediately. Any calculation still in flight is now superseded and its
    // result will be discarded, so there is nothing to wait for — holding the button disabled for
    // up to the full request budget would only make the user wait out a result they cannot use.
    setCalculateLoading(false);
  };

  const calculateLeads = async (request: ICalculateLeadsRequest) => {
    // The inputs stay enabled while a calculation runs, so the selection can change before the
    // response lands. Claim a generation up front and only write state if it is still current.
    const requestId = ++leadsRequestIdRef.current;
    setCalculateLoading(true);
    try {
      // Explicit timeout: the shared axios instance sets none, so a hung request would never
      // settle, the finally would never run, and calculateLoading would disable the button for
      // the rest of the session. Sits above API Gateway's 29s cutoff so the server's own
      // timeout response wins whenever there is one.
      const response = await axiosPost('/contact-lists-calculate-leads', request, { timeout: 35_000 });

      if (requestId !== leadsRequestIdRef.current) {
        return;
      }

      if (response && response.status === 200) {
        setLeads(response?.data?.data);
      }
    } catch (error: any) {
      console.error(error);

      // Superseded: the user changed the selection while this was in flight, so the failure refers
      // to a calculation they have already abandoned. Logged above, but not surfaced.
      if (requestId !== leadsRequestIdRef.current) {
        return;
      }

      // A gateway timeout carries no CORS headers, so error.response is undefined there — fall back
      // rather than reading it unguarded. Any volume already on screen is deliberately left as it is.
      addNotification({ content: error?.response?.data?.message ?? 'Failed to calculate campaign volume. Please try again.', type: NotificationType.FAILURE });
    } finally {
      // Only the current generation owns the flag. A superseded calculation must not clear it,
      // because either invalidateLeads already did, or a newer calculation is now running and
      // still needs the spinner. Cannot strand the button: whichever generation is current
      // always settles, and its own request carries a 35s timeout.
      if (requestId === leadsRequestIdRef.current) {
        setCalculateLoading(false);
      }
    }
  };

  const uploadMediaFile = async (
    file: File,
    numberType: SystemNumberTypeEnum,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const validateFileSize = numberType === SystemNumberTypeEnum.LOCAL ? isFileLocalSize : isFileTollFreeSize;

    if (!file || !isFileMediaType(file) || !validateFileSize(file)) {
      setFieldValue('mediaUrl', '');
      setFieldValue('mediaName', '');
      return;
    }

    const mediaFileKey: string = uuid();

    const s3SignedUrlRequest: ISignedUrlRequest = {
      bucket: BUCKETS.MMS,
      key: mediaFileKey,
      action: 'putObject',
      contentType: file.type,
    };

    const s3MediaUrlRequest: ISignedUrlRequest = {
      bucket: BUCKETS.MMS,
      key: mediaFileKey,
      action: 'getObject',
      contentType: file.type,
      expiresIn: 31536000, // 1 Year
    };

    try {
      setLoading(true);
      const {
        data: { data: uploadUrl },
      } = await axiosPost<IHttpResponse<string>>('/utility-get-signed-s3-url', s3SignedUrlRequest);

      await axios.put(uploadUrl, file, {
        headers: {
          'Content-Type': file.type,
          'Content-Disposition': 'attachment',
        },
      });

      const baseUrl =
        window.location.hostname === 'localhost'
          ? `https://dev.${getProductBaseDomainUrl()}`
          : auth0Config?.apiUrl.replace('api.', '');
      setFieldValue('mediaUrl', `${baseUrl}/mms-uploads/${mediaFileKey}`);
      setFieldValue('mediaName', file.name);
    } catch (err) {
      console.error('Error when attempting to upload media file');
    } finally {
      setLoading(false);
    }
  };

  if (allListsFailed) {
    errors.contactLists = 'Selected contact list failed to process and has been removed form the campaign';
  }

  return (
    <div className="flex flex-wrap space-y-8">
      <div className="campaign-plan">
        <h2 className="mb-8 text-black basis-full">Plan your message</h2>
        <FastField
          component={TextInput}
          id="name"
          name="name"
          label="Campaign Title"
          placeholder="Name your campaign"
          value={values.name}
          error={touched.name ? errors.name : ''}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        <ClientDropdown
          placeholder="Type to search for a client"
          onChange={async (newValue) => {
            await setFieldValue!('client', newValue);
            await setFieldTouched!('client', true);
            invalidateLeads();
            if (!newValue || (newValue as IDropdownValue).value !== values.client?.value) {
              setFieldValue!('campaignType', null);
              setFieldValue!('preferredAreaCode', null);
            }

            // if clicker group is already set either by user or campaign has already been saved as draft
            // do not reset it to default clicker group of client
            if (!values.clickerGroup) {
              setFieldValue!('clickerGroup', (newValue as IDropdownValue)?.additionalData?.clickerGroup ?? '');
              setFieldTouched!('clickerGroup', true);
            }

            if (!(newValue as IDropdownValue)?.additionalData?.twoWayEnabled) {
              setFieldValue!('twoWayEnabled', false);
              setFieldTouched!('twoWayEnabled', true);
            }
          }}
          value={values.client}
          onBlur={() => {
            setFieldTouched!('client', true);
          }}
          errorMessage={(errors?.client as any)?.value as string}
          showError={(touched?.client as any) ?? false}
        />
        <Radio
          label="Message Type"
          disabled={values.status !== 'DRAFT'}
          options={[
            {
              label: 'SMS',
              value: MessageTypeEnum.SMS,
            },
            {
              label: 'MMS',
              value: MessageTypeEnum.MMS,
            },
          ]}
          value={values.messageType}
          onChange={(newValue) => {
            setFieldValue!('messageType', newValue);
            setFieldTouched!('messageType', true);
            setFieldValue!('campaignType', undefined);
            setFieldValue!('preferredAreaCode', null);
            invalidateLeads();
          }}
          grow
          error={touched.messageType && errors.messageType?.value}
        />
        <NewCampaignTypeDropdown
          options={!loading && values.client ? dropdownValues : []}
          value={values.campaignType}
          loading={loading}
          onChange={(newValue: IDropdownValue | undefined) => {
            setFieldValue!('campaignType', newValue?.value);
            invalidateLeads();
            const selectedAreaCodes: IDropdownValue[] =
              campaignTypes
                .find((ct) => ct.campaignType === newValue?.value)
                ?.areaCodes?.map((ac) => ({
                  label: ac.areaCode,
                  value: ac.areaCode,
                })) ?? [];
            if (selectedAreaCodes?.length <= 1) {
              setFieldValue!('preferredAreaCode', selectedAreaCodes[0]);
            } else {
              setFieldValue!('preferredAreaCode', null);
            }
          }}
          onBlur={() => {
            setFieldTouched!('campaignType', true);
          }}
          touched={touched.campaignType}
          errorMessage={!loading ? errors.campaignType : ''}
        />
        <PreferredAreaCodeDropdown
          value={values.preferredAreaCode}
          onChange={(newValue) => {
            setFieldValue!('preferredAreaCode', newValue);
          }}
          disabled={
            !values.client ||
            !values.campaignType ||
            preferredAreaCodeOptions?.length === 0 ||
            (preferredAreaCodeOptions?.length <= 1 && preferredAreaCodeOptions[0]?.value === PreferredAreaCodeEnum.TOLL_FREE)
          }
          error={errors.preferredAreaCode}
          options={preferredAreaCodeOptions}
        />
        {values.messageType === MessageTypeEnum.MMS ? (
          <>
            <div>
              <FileUpload
                id="mediaFile"
                name="mediaFile"
                label="Media File"
                fileName={values.mediaName}
                accepts="image/*, video/*, .pdf, .vcf"
                value={values.mediaFile}
                error={errors.mediaFile as any}
                onBlur={handleBlur}
                placeholder="Select a media file"
                onChange={(fileUploadEvent: any) => {
                  const file = fileUploadEvent.target.files[0];
                  setFieldTouched!('mediaFile', true);
                  setFieldValue!('mediaFile', file);
                  if (file.type.match('video.*')) {
                    setFieldValue!('isMMSVideo', true);
                  } else {
                    setFieldValue!('isMMSVideo', false);
                  }
                  uploadMediaFile(file, SystemNumberTypeEnum.LOCAL, setFieldValue!); // TODO: Remove hardcode of local number type so file size is accurate
                }}
              />
              {!values.mediaFile && <p className="text-sm text-amber-600 mt-1">For best deliverability, we recommend keeping media files below 500KB</p>}
            </div>
          </>
        ) : (
          <div>{/* SPACER */}</div>
        )}
        <div className="w-full border-b-2 border-light-gray shrink-0"></div>
        <h2 className="mb-8 text-black basis-full">Select Contacts</h2>
        <NewContactListDropdown
          value={values.contactLists}
          onChange={(newValue) => {
            setFieldValue!('contactLists', newValue);
            invalidateLeads();
          }}
          onBlur={() => {
            setFieldTouched!('contactLists', true);
          }}
          errorMessage={errors.contactLists as string}
          showError={!!(touched.contactLists && errors.contactLists) || allListsFailed}
        />

        <NewAssignedAgentsDropdown
          value={values.clickerGroup ?? undefined}
          onChange={(newValue) => {
            // Explicitly set empty string for "None" selection to ensure form value is updated
            setFieldValue!('clickerGroup', newValue?.value ?? '');
            setFieldTouched!('clickerGroup', true);
          }}
          onBlur={() => {
            setFieldTouched!('clickerGroup', true);
          }}
          errorMessage={errors.clickerGroup as string}
          showError={!!(touched.client && errors.clickerGroup)}
          includeNone={!values.client?.additionalData?.clickerGroup}
        />
        <FastField
          component={TextInput}
          id="externalId"
          name="externalId"
          label={
            <div className="flex items-center mb-5">
              <h5>{'EXTERNAL ID (OPTIONAL)'}</h5>
              <Info className="ml-1" text="Include an ID from an external source." />
            </div>
          }
          value={values.externalId}
          errors={errors.externalId}
          touched={touched.externalId}
          onChange={handleChange}
        />
        <Radio
          label="DO YOU NEED TO SUPPRESS CONTACTS FROM A LIST OR AREA?"
          options={[
            {
              label: 'Yes',
              value: true,
            },
            {
              label: 'No',
              value: false,
            },
          ]}
          value={values.showSuppressions}
          grow
          onChange={(newValue) => {
            setFieldValue!('showSuppressions', newValue);
          }}
        />
        {values.showSuppressions && (
          <>
            <h2 className="mb-8 text-black basis-full">Contact Suppression</h2>
            <NewSuppressionListDropdown
              value={values.suppressionLists}
              contactListIds={values.contactLists.map((dv) => dv.value)}
              onChange={(newValue) => {
                setFieldValue!('suppressionLists', newValue);
                invalidateLeads();
              }}
              onBlur={() => {
                setFieldTouched!('suppressionLists', true);
              }}
              errorMessage={errors.suppressionLists as string}
              showError={!!(touched.suppressionLists && errors.suppressionLists)}
            />
            <div className="flex w-full gap-x-8">
              <NewStateDropdown
                value={values.suppressionStates}
                onChange={(newValue) => {
                  setFieldValue!('suppressionStates', newValue);
                  invalidateLeads();
                }}
                onBlur={() => {
                  setFieldTouched!('suppressionStates', true);
                }}
                errorMessage={errors.suppressionStates as string}
                showError={!!(touched.suppressionStates && errors.suppressionStates)}
              />
              <AreaCodeDropdown
                value={values.suppressionAreaCodes}
                onChange={(newValue) => {
                  setFieldValue!('suppressionAreaCodes', newValue);
                  invalidateLeads();
                }}
                onBlur={() => {
                  setFieldTouched!('suppressionAreaCodes', true);
                }}
                errorMessage={errors.suppressionAreaCodes as string}
                showError={!!(touched.suppressionAreaCodes && errors.suppressionAreaCodes)}
              />
            </div>
          </>
        )}
        <div className="flex justify-between">
          <div className="flex items-center">
            <Button
              variant={ButtonVariantEnum.GREEN}
              onClick={() =>
                calculateLeads({
                  clientId: values.client?.value,
                  contactListIds: values.contactLists?.map((cl) => cl.value),
                  suppressionListIds: values.suppressionLists?.map((sl) => sl.value),
                  suppressionAreaCodes: values.suppressionAreaCodes?.map((sa) => sa?.additionalData?.toString()),
                  suppressionStateCodes: values.suppressionStates?.map((ss) => ss.value),
                  campaignType: values.campaignType,
                  campaignMessageType: values.messageType,
                })
              }
              disabled={!values.campaignType || values.contactLists?.length <= 0 || calculateLoading}
              type="button"
              trailingIcon={
                <div className={calculateLoading ? 'spin' : ''}>
                  <CalculateIcon />
                </div>
              }
            >
              Calculate Campaign Volume
            </Button>
            {leads !== undefined && <h3 className="ml-8 text-wc-blue">{leads} messages</h3>}
          </div>
          {submissionError && <h3 className="flex items-center mt-3 text-red-600">{submissionError}</h3>}
        </div>
      </div>
    </div>
  );
};

export default Plan;
